/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { tokens } from "../../theme";
import { UserContext } from "../../contexts";
import Header from "../../components/Header";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Switch,
} from "@mui/material";
import Select from "../../components/SearchableSelect";
import DataGridButton from "../../components/DataGridButton";
import DataTable from "../../components/DataTable";
import StatusChip, {
  getStatusLabel,
  getStatusColors,
} from "../../components/StatusChip";
import CloudIcon from "../../components/CloudIcon";
import { Add, Close, Sync, ChevronRight } from "@mui/icons-material";
import SearchWithHistory from "../../components/SearchWithHistory";
import Cache from "../../utils/Cache";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import { closeButtonStyles } from "../../styles/buttonStyles";
import {
  tenantInstancesUrl,
  patchEnv,
  useGetEnvs,
  useGetUsers,
  useRequestCP,
  useHibernateEnv,
  useWakeUpEnv,
  useDeleteEnv,
  useRefreshEnv,
  useConvertToVPTC,
  useRequestBYOA,
  useAddLicences,
} from "../../api/envs";
import Notification from "../../components/Notification";
import TimeCell from "../../components/TimeCell";
import ConvoyDialog from "../../components/ConvoyDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import useResizableDrawerWidth from "../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../components/DrawerSnapToggle";
import MembersList from "./MembersList";
import ScheduledConfigs from "./ScheduledConfigs";
import RowActions from "./RowActions";
import DetailsDrawer from "./DetailsDrawer";
import { rowOpenStyles } from "./DetailsDrawer/styles";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import RefreshDrawer from "./Dialog/RefreshDrawer";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";
import BYOASubscriptionsModal from "./Dialog/BYOASubscriptionsModal";
import RequestCPDialogContent from "./Dialog/RequestCPDialogContent";
import RequestBYOADialogContent from "./Dialog/RequestBYOADialogContent";
import WakeupDialogContent from "./Dialog/WakeUpDialogContent";
import {
  isValidRequestCPPayload,
  roles,
  notifyMinOptions,
  parseRequestCPPayload,
  getEnvsSimplified,
  getEnvStatusCount,
  getFilterOptions,
  getFilteredEnvs,
  ALL_FILTER,
  envFilterCacheKeys,
  compareReleaseLabels,
} from "./utils";
import {
  dataGridButtonStyles,
  envListButtonStyles,
} from "../../styles/buttonStyles";
import { convertLocalTimeToUTCTime } from "../../utils/dateUtils";
import { indexStyles, requestCPDialogStyles } from "./styles";

const Environments = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const navigate = useNavigate();

  const [envs, setEnvs] = useState([]);
  const [updates, setUpdates] = useState({});
  const [idToIndexMap, setIdToIndexMap] = useState({});
  const [envsWithUpdates, setEnvsWithUpdates] = useState([]);
  const [isSavingUpdates, setIsSavingUpdates] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => Cache.get(envFilterCacheKeys.status) || ALL_FILTER
  );
  const [createdByFilter, setCreatedByFilter] = useState(
    () => Cache.get(envFilterCacheKeys.createdBy) || ALL_FILTER
  );
  // memberRole is hidden out of the box; anything the user explicitly
  // toggles afterward (including turning memberRole back on) overrides this.
  const DEFAULT_COLUMN_VISIBILITY = { memberRole: false };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    try {
      const cached = JSON.parse(Cache.get(envFilterCacheKeys.columnVisibility));
      return cached
        ? { ...DEFAULT_COLUMN_VISIBILITY, ...cached }
        : DEFAULT_COLUMN_VISIBILITY;
    } catch {
      return DEFAULT_COLUMN_VISIBILITY;
    }
  });
  const handleColumnVisibilityChange = (model) => {
    setColumnVisibilityModel(model);
    Cache.set(envFilterCacheKeys.columnVisibility, JSON.stringify(model));
  };

  // null = no saved order yet, so the table's own declared column order wins.
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      return JSON.parse(Cache.get(envFilterCacheKeys.columnOrder));
    } catch {
      return null;
    }
  });
  // "details"/"actions" are structural (the row-open toggle and row actions)
  // and stay pinned at the start/end; DataGridPro's own `disableReorder` on
  // those two columns already blocks dragging them, this only needs to keep
  // them out of the persisted order so a stale cache entry can't reintroduce
  // them somewhere else.
  const handleColumnOrderChange = ({ column, targetIndex }) => {
    setColumnOrder((prevOrder) => {
      const currentOrder = (prevOrder || columns.map((c) => c.field)).filter(
        (field) => field !== "details" && field !== "actions"
      );
      const from = currentOrder.indexOf(column.field);
      if (from === -1) return prevOrder;
      const next = [...currentOrder];
      next.splice(from, 1);
      // targetIndex is relative to the full grid (details is always index 0),
      // so shift it back into the reorderable-only list built above.
      next.splice(Math.max(targetIndex - 1, 0), 0, column.field);
      Cache.set(envFilterCacheKeys.columnOrder, JSON.stringify(next));
      return next;
    });
  };

  // Dragging inside the Columns menu only reorders the toggleable fields
  // (the menu excludes "name", which isn't hideable but is still a
  // reorderable table column) — so re-slot just those fields into the full
  // order, leaving "name"'s current position untouched.
  const handleMenuReorder = (newToggleableOrder) => {
    setColumnOrder((prevOrder) => {
      const currentOrder = (prevOrder || columns.map((c) => c.field)).filter(
        (field) => field !== "details" && field !== "actions"
      );
      const toggleableSet = new Set(newToggleableOrder);
      let i = 0;
      const next = currentOrder.map((field) =>
        toggleableSet.has(field) ? newToggleableOrder[i++] : field
      );
      Cache.set(envFilterCacheKeys.columnOrder, JSON.stringify(next));
      return next;
    });
  };

  const handleResetColumnSettings = () => {
    setColumnVisibilityModel(DEFAULT_COLUMN_VISIBILITY);
    setColumnOrder(null);
    Cache.remove(envFilterCacheKeys.columnVisibility);
    Cache.remove(envFilterCacheKeys.columnOrder);
  };

  const [toShowAllEnvs, setToShowAllEnvs] = useState(false);
  const [detailsEnvId, setDetailsEnvId] = useState(null);

  const loggedInUser = useContext(UserContext);
  // Read-only details drawer. Reflected in the URL (?details=<id>) so a
  // reload or a shared link reopens it on the right row.
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewDetailsEnvId, setViewDetailsEnvIdState] = useState(
    () => searchParams.get("details") || null
  );
  const setViewDetailsEnvId = (id) => {
    setViewDetailsEnvIdState(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("details", id);
        else next.delete("details");
        return next;
      },
      { replace: !id }
    );
  };

  const nameOptions = useMemo(
    () => getFilterOptions(envsWithUpdates, "name"),
    [envsWithUpdates]
  );
  const statusOptions = useMemo(
    () => getFilterOptions(envsWithUpdates, "status"),
    [envsWithUpdates]
  );
  const createdByOptions = useMemo(
    () => getFilterOptions(envsWithUpdates, "createdBy"),
    [envsWithUpdates]
  );

  const filteredEnvs = useMemo(
    () =>
      getFilteredEnvs({
        envs: envsWithUpdates,
        searchText,
        statusFilter,
        createdByFilter,
      }),
    [envsWithUpdates, searchText, statusFilter, createdByFilter]
  );

  const statusCounts = useMemo(
    () => getEnvStatusCount(filteredEnvs),
    [filteredEnvs]
  );

  useEffect(() => {
    if (!envsWithUpdates.length) return;
    if (statusFilter !== ALL_FILTER && !statusOptions.includes(statusFilter))
      setStatusFilter(ALL_FILTER);
    if (
      createdByFilter !== ALL_FILTER &&
      !createdByOptions.includes(createdByFilter)
    )
      setCreatedByFilter(ALL_FILTER);
  }, [statusOptions, createdByOptions]);

  useEffect(() => {
    Cache.set(envFilterCacheKeys.status, statusFilter);
    Cache.set(envFilterCacheKeys.createdBy, createdByFilter);
  }, [statusFilter, createdByFilter]);

  const { postData: getUsers, response: getUsersResp } = useGetUsers();

  const {
    postData: getEnvs,
    response: getEnvsResp,
    isLoading: isLoadingEnvs,
  } = useGetEnvs();

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    getEnvs(showAllEnvs(toShowAllEnvs));
  }, [toShowAllEnvs]);

  useEffect(() => {
    if (Array.isArray(getUsersResp) && Array.isArray(getEnvsResp?.response)) {
      const envsSimplified = getEnvsSimplified(
        getEnvsResp.response,
        getUsersResp
      );

      setEnvs(structuredClone(envsSimplified));
      checkIfUpdatesIsEmpty();
      setEnvsWithUpdates(mergeEnvsWithUpdates({ passedEnvs: envsSimplified }));

      const newMap = {};
      envsSimplified.forEach((env, index) => (newMap[env["id"]] = index));
      setIdToIndexMap(newMap);

      setRefreshingEnvIds((prev) => {
        if (!prev.size) return prev;
        const next = new Set(prev);
        envsSimplified.forEach((env) => {
          if (next.has(env?.id) && env?.status !== "RUNNING")
            next.delete(env.id);
        });
        return next.size === prev.size ? prev : next;
      });
    }
  }, [getEnvsResp, getUsersResp]);

  const mergeEnvsWithUpdates = ({ passedUpdates, passedEnvs } = {}) => {
    const localEnvs = [...(passedEnvs || envs)];

    const updatesToUse = passedUpdates !== undefined ? passedUpdates : updates;
    Object.keys(updatesToUse).forEach((envId) => {
      const updatesInEnv = updatesToUse[envId];
      const idx = idToIndexMap[envId];
      const env = { ...localEnvs[idx] };

      if ("autoHibernateEnabled" in updatesInEnv) {
        env.autoHibernateEnabled = updatesInEnv?.autoHibernateEnabled;
      }
      if ("autoHibernateSchedule" in updatesInEnv) {
        env.autoHibernateSchedule = updatesInEnv?.autoHibernateSchedule;
      }
      if ("autoHibernateRepeat" in updatesInEnv) {
        env.autoHibernateRepeat = updatesInEnv?.autoHibernateRepeat;
      }
      if ("autoHibernateNotify" in updatesInEnv) {
        env.autoHibernateNotify = updatesInEnv?.autoHibernateNotify;
      }
      if ("autoWakeupEnabled" in updatesInEnv) {
        env.autoWakeupEnabled = updatesInEnv?.autoWakeupEnabled;
      }
      if ("autoWakeupSchedule" in updatesInEnv) {
        env.autoWakeupSchedule = updatesInEnv?.autoWakeupSchedule;
      }
      if ("autoWakeupRepeat" in updatesInEnv) {
        env.autoWakeupRepeat = updatesInEnv?.autoWakeupRepeat;
      }
      if ("autoWakeupNotify" in updatesInEnv) {
        env.autoWakeupNotify = updatesInEnv?.autoWakeupNotify;
      }

      let members = { ...env?.members };
      let indexedMembers = [...env?.indexedMembers];
      if ("membersAdded" in updatesInEnv) {
        updatesInEnv?.addedMembersIndex?.forEach((email) => {
          members[email] = updatesInEnv?.membersAdded.get(email);
          if (!indexedMembers.includes(email)) {
            indexedMembers.push(email);
          }
        });
      }
      if ("membersDeleted" in updatesInEnv) {
        updatesInEnv?.membersDeleted?.forEach((email) => {
          delete members[email];
        });
      }
      if ("membersModified" in updatesInEnv) {
        updatesInEnv?.membersModified?.forEach((role, email) => {
          members[email] = role;
        });
      }
      env.members = members;
      env.indexedMembers = indexedMembers;
      localEnvs[idx] = env;
    });
    return localEnvs;
  };

  const checkIfUpdatesIsEmpty = (passedUpdates) => {
    const updatesToUse = passedUpdates !== undefined ? passedUpdates : updates;
    let empty = true;
    Object.keys(updatesToUse).forEach((envId) => {
      const updatesInEnv = updatesToUse[envId];
      updatesInEnv.hasUpdates = false;
      updatesInEnv.hasUpdatesInMembers = false;
      updatesInEnv.hasConfigUpdates = false;
      if (
        updatesInEnv?.membersAdded?.size ||
        updatesInEnv?.membersDeleted?.size ||
        updatesInEnv?.membersModified?.size
      ) {
        updatesInEnv.hasUpdatesInMembers = true;
      }
      if (
        "autoHibernateEnabled" in updatesInEnv ||
        "autoHibernateSchedule" in updatesInEnv ||
        "autoHibernateRepeat" in updatesInEnv ||
        "autoHibernateNotify" in updatesInEnv ||
        "autoWakeupEnabled" in updatesInEnv ||
        "autoWakeupSchedule" in updatesInEnv ||
        "autoWakeupRepeat" in updatesInEnv ||
        "autoWakeupNotify" in updatesInEnv
      ) {
        updatesInEnv.hasConfigUpdates = true;
      }
      if (updatesInEnv?.hasUpdatesInMembers || updatesInEnv?.hasConfigUpdates) {
        updatesInEnv.hasUpdates = true;
        empty = false;
      }
    });
    return empty;
  };

  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("");

  const [dialog, setDialog] = useState({
    selectedEnv: {},
    context: "",
    title: "",
    isOpen: false,
    content: <></>,
  });

  const [refreshDialog, setRefreshDialog] = useState({
    selectedEnv: {},
    isOpen: false,
  });

  const [refreshingEnvIds, setRefreshingEnvIds] = useState(() => new Set());

  const [byoaModal, setByoaModal] = useState({
    selectedEnv: {},
    isOpen: false,
  });

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: "",
    content: "",
    handleDialogConfirm: () => {},
  });

  const [requestCPPayload, setRequestCPPayload] = useState({});
  const [requestCPDrawerOpen, setRequestCPDrawerOpen] = useState(false);
  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: requestCPDrawerWidth,
    isWide: isRequestCPDrawerWide,
    toggleWide: toggleRequestCPDrawerWide,
    handleProps: requestCPResizeProps,
  } = useResizableDrawerWidth({
    storageKey: "request-control-plane",
    defaultWidth: 480,
    maxWidth: 900,
    enabled: isDesktopDrawer,
  });
  // 1080 is the width the members pane (minmax(0, 600px)) and the schedule
  // pane (fixed 480px) were sized to add up to exactly; widening past it only
  // helps once detailsDrawerBody's member column can grow past that 600px cap.
  const {
    width: detailsDrawerWidth,
    isWide: isDetailsDrawerWide,
    toggleWide: toggleDetailsDrawerWide,
    handleProps: detailsResizeProps,
  } = useResizableDrawerWidth({
    storageKey: "environment-details",
    defaultWidth: 1080,
    maxWidth: 1500,
    enabled: isDesktopDrawer,
  });
  const [requestBYOAPayload, setRequestBYOAPayload] = useState({});

  const resetDialog = () => {
    setDialog({
      selectedEnv: {},
      context: "",
      title: "",
      isOpen: false,
      content: <></>,
    });
    setConfirmationDialog({
      isOpen: false,
      title: "",
      content: "",
      handleDialogConfirm: () => {},
    });
  };

  const {
    postData: requestCP,
    response: requestCPResp,
    responseStatus: requestCPRespStatus,
    isLoading: isSubmittingCP,
  } = useRequestCP();

  const {
    postData: hibernate,
    response: hibResp,
    responseStatus: hibRespStatus,
  } = useHibernateEnv();

  const {
    postData: wakeup,
    response: wakeupResp,
    responseStatus: wakeupRespStatus,
  } = useWakeUpEnv();

  const refreshEnvState = useRefreshEnv();
  const {
    postData: refreshEnv,
    response: refreshEnvResp,
    responseStatus: refreshEnvRespStatus,
  } = refreshEnvState;

  const {
    postData: convertToVPTC,
    response: convertToVPTCResp,
    responseStatus: convertToVPTCRespStatus,
  } = useConvertToVPTC();

  const {
    postData: requestBYOA,
    response: requestBYOAResp,
    responseStatus: requestBYOARespStatus,
  } = useRequestBYOA();

  const {
    postData: addLicences,
    response: addLicencesResp,
    responseStatus: addLicencesRespStatus,
  } = useAddLicences();

  const {
    postData: deleteEnv,
    response: deleteEnvResp,
    responseStatus: deleteEnvRespStatus,
  } = useDeleteEnv();

  const handleDialogConfirm = () => {
    const url = dialog.selectedEnv?.url;
    switch (dialog.context) {
      case "wakeUp":
        wakeup({ url });
        break;
      case "requestBYOA":
        requestBYOA(requestBYOAPayload);
        break;
      default:
        break;
    }
    resetDialog();
  };

  useEffect(() => {
    const message =
      requestCPResp?.message ||
      requestCPResp?.detail?.message ||
      JSON.stringify(requestCPResp?.detail?.[0]);
    if (message?.length && typeof requestCPRespStatus === "number") {
      setMessage(message);
      setSeverity(requestCPRespStatus === 200 ? "success" : "error");
      setNotify(true);
    }
    if (
      typeof requestCPRespStatus === "number" &&
      requestCPRespStatus >= 200 &&
      requestCPRespStatus < 300
    ) {
      setRequestCPDrawerOpen(false);
    }
  }, [requestCPResp, requestCPRespStatus]);

  useEffect(() => {
    const message = hibResp?.message || hibResp?.detail?.message;
    if (message?.length && typeof hibRespStatus === "number") {
      setMessage(message);
      setSeverity(hibRespStatus === 200 ? "success" : "error");
      setNotify(true);
      if (hibRespStatus === 200) {
        getEnvs(showAllEnvs(toShowAllEnvs));
      }
    }
  }, [hibResp, hibRespStatus]);

  useEffect(() => {
    const message = wakeupResp?.message || wakeupResp?.detail?.message;
    if (message?.length && typeof wakeupRespStatus === "number") {
      setMessage(message);
      setSeverity(wakeupRespStatus === 200 ? "success" : "error");
      setNotify(true);
      if (wakeupRespStatus === 200) {
        getEnvs(showAllEnvs(toShowAllEnvs));
      }
    }
  }, [wakeupResp, wakeupRespStatus]);

  useEffect(() => {
    const message = refreshEnvResp?.message || refreshEnvResp?.detail?.message;
    if (message?.length && typeof refreshEnvRespStatus === "number") {
      setMessage(message);
      setSeverity(refreshEnvRespStatus === 200 ? "success" : "error");
      setNotify(true);
      if (refreshEnvRespStatus === 200) {
        getEnvs(showAllEnvs(toShowAllEnvs));
      } else {
        // Non-2xx response: clear the in-flight marker so the row unlocks.
        setRefreshingEnvIds(new Set());
      }
    }
  }, [refreshEnvResp, refreshEnvRespStatus]);

  useEffect(() => {
    // Network-level failure: useLazyPost sets error but leaves responseStatus
    // stale, so the row would stay locked without this separate clear.
    if (refreshEnvState?.error) {
      setRefreshingEnvIds(new Set());
    }
  }, [refreshEnvState?.error]);

  useEffect(() => {
    const message =
      convertToVPTCResp?.message || convertToVPTCResp?.detail?.message;
    if (message?.length && typeof convertToVPTCRespStatus === "number") {
      setMessage(message);
      setSeverity(convertToVPTCRespStatus === 200 ? "success" : "error");
      setNotify(true);
      if (convertToVPTCRespStatus === 200) {
        getEnvs(showAllEnvs(toShowAllEnvs));
      }
    }
  }, [convertToVPTCResp, convertToVPTCRespStatus]);

  useEffect(() => {
    const message =
      requestBYOAResp?.message || requestBYOAResp?.detail?.message;
    if (message?.length && typeof requestBYOARespStatus === "number") {
      setMessage(message);
      setSeverity(requestBYOARespStatus === 200 ? "success" : "error");
      setNotify(true);
    }
  }, [requestBYOAResp, requestBYOARespStatus]);

  useEffect(() => {
    const message =
      addLicencesResp?.message || addLicencesResp?.detail?.message;
    if (message?.length && typeof addLicencesRespStatus === "number") {
      setMessage(message);
      setSeverity(addLicencesRespStatus === 200 ? "success" : "error");
      setNotify(true);
      if (addLicencesRespStatus === 200) {
        getEnvs(showAllEnvs(toShowAllEnvs));
      }
    }
  }, [addLicencesResp, addLicencesRespStatus]);

  useEffect(() => {
    const message =
      deleteEnvResp?.message ||
      deleteEnvResp?.detail?.message ||
      deleteEnvResp?.detail;
    if (message?.length && typeof deleteEnvRespStatus === "number") {
      setMessage(message);
      setSeverity(deleteEnvRespStatus === 200 ? "success" : "error");
      setNotify(true);
      if (deleteEnvRespStatus === 200) {
        getEnvs(showAllEnvs(toShowAllEnvs));
      }
    }
  }, [deleteEnvResp, deleteEnvRespStatus]);

  const handleHibernateWakeUp = (env, action) => {
    if (action === "HIBERNATE") {
      const url = env?.url;
      setConfirmationDialog({
        isOpen: true,
        title: "Hibernate Environment",
        content: (
          <>
            You are going to hibernate the environment{" "}
            <strong>{env?.name}</strong>. Are you sure?
          </>
        ),
        handleDialogConfirm: () => {
          hibernate({ url });
        },
      });
    } else {
      setDialog({
        selectedEnv: env,
        context: "wakeUp",
        title: "Wake Up environment",
        isOpen: true,
      });
    }
  };

  const handleRefresh = (env) => {
    setRefreshDialog({
      selectedEnv: env,
      isOpen: true,
    });
  };

  // The backend takes a while to move the env out of RUNNING, so the row is
  // marked locally until a poll returns a non-RUNNING status for it.
  const handleRefreshSubmit = (payload, env) => {
    if (env?.id !== undefined) {
      setRefreshingEnvIds((prev) => new Set(prev).add(env.id));
    }
    refreshEnv(payload);
  };

  const handleConvertToVPTC = (env) => {
    const url = env?.url;
    setConfirmationDialog({
      isOpen: true,
      title: "Convert Environment to VPT@C",
      content: (
        <>
          You are going to convert the environment <strong>{env?.name}</strong>{" "}
          to VPT@C
        </>
      ),
      handleDialogConfirm: () => {
        convertToVPTC({ url });
      },
    });
  };

  const handleAutomationRuns = (env) => {
    navigate(`/tenants/${env?.url}/automation-runs`, {
      state: { tenantUrl: env?.url },
    });
  };

  const handleAddLicences = (env) => {
    const url = env?.url;
    setConfirmationDialog({
      isOpen: true,
      title: "Add Licences",
      content: (
        <>
          You are going to add Licences for the <strong>{env?.name}</strong>
        </>
      ),
      handleDialogConfirm: () => {
        addLicences({ url });
      },
    });
  };

  const handleDelete = useCallback((env) => {
    const url = env?.url;
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Environment",
      content: (
        <>
          You are going to delete the environment <strong>{env?.name}</strong>.
          Are you sure?
        </>
      ),
      handleDialogConfirm: () => {
        deleteEnv({ url });
      },
    });
  });

  const handleRequestCP = () => {
    setRequestCPDrawerOpen(true);
  };

  // Backdrop clicks are ignored so a half-filled request isn't lost by accident.
  const closeRequestCPDrawer = (_event, reason) => {
    if (reason === "backdropClick") return;
    setRequestCPDrawerOpen(false);
  };

  const submitRequestCP = () => {
    requestCP(parseRequestCPPayload(requestCPPayload));
  };

  const showAllEnvs = (toShow) => (toShow ? "?show_all=true" : "");

  const createReqBodyForEnvId = (envId) => {
    const envWithUpdates = envsWithUpdates[idToIndexMap[envId]];
    const updatesInEnv = updates[envId];
    const reqBody = {};

    if (updatesInEnv?.hasUpdatesInMembers) {
      const members = envWithUpdates?.indexedMembers
        ?.filter((email) => email in envWithUpdates?.members)
        ?.map((email) => {
          return {
            email: email,
            role_name: roles[envWithUpdates?.members[email]],
          };
        });
      reqBody.members = members;
    }

    if (updatesInEnv?.hasConfigUpdates) {
      const config = { ...envWithUpdates?.config };
      delete config.dbServicesAutoDelete;

      config.hibernate.enableAutomaticHibernation =
        envWithUpdates?.autoHibernateEnabled;
      config.hibernate.schedule.utctime = convertLocalTimeToUTCTime(
        envWithUpdates?.autoHibernateSchedule
      );
      config.hibernate.schedule.repeat =
        envWithUpdates?.autoHibernateRepeat?.reduce(
          (out, bool, index) => (bool ? out.concat(index) : out),
          []
        );
      config.hibernate.schedule.notifyBeforeMinutes =
        envWithUpdates?.autoHibernateNotify.reduce(
          (out, bool, index) =>
            bool ? out.concat(notifyMinOptions[index]) : out,
          []
        );

      config.wakeup.enableAutomaticWakeup = envWithUpdates?.autoWakeupEnabled;
      config.wakeup.schedule.utctime = convertLocalTimeToUTCTime(
        envWithUpdates?.autoWakeupSchedule
      );
      config.wakeup.schedule.repeat = envWithUpdates?.autoWakeupRepeat?.reduce(
        (out, bool, index) => (bool ? out.concat(index) : out),
        []
      );
      config.wakeup.schedule.notifyBeforeMinutes =
        envWithUpdates?.autoWakeupNotify?.reduce(
          (out, bool, index) =>
            bool ? out?.concat(notifyMinOptions[index]) : out,
          []
        );
      reqBody.config = config;
    }
    return reqBody;
  };

  const saveUpdates = (onlyEnvId) => {
    setIsSavingUpdates(true);
    const envIdsWithUpdates = Object.keys(updates).filter(
      (envId) =>
        updates[envId].hasUpdates &&
        (onlyEnvId === undefined || envId === String(onlyEnvId))
    );
    const succeededPatches = [];
    const failedPatches = [];
    const patchCalls = [];
    envIdsWithUpdates.forEach((envId) =>
      patchCalls.push(
        patchEnv(
          `${tenantInstancesUrl}/${envs[idToIndexMap[envId]].url}`,
          createReqBodyForEnvId(envId)
        )
      )
    );

    Promise.all(patchCalls).then((responses) => {
      responses.forEach((response, index) => {
        const envId = envIdsWithUpdates[index];
        const envName = envs[idToIndexMap[envId]].name;
        if (response.status === 200) {
          succeededPatches.push(envId);
        } else {
          failedPatches.push(envName);
        }
      });

      setUpdates((prev) => {
        const currUpdates = { ...prev };
        succeededPatches.forEach((envId) => delete currUpdates[envId]);
        return currUpdates;
      });

      if (failedPatches.length > 0) {
        setMessage(`Updates for envs failed: ${failedPatches.join(", ")}`);
        setSeverity("error");
      } else {
        setMessage("Successfully updated");
        setSeverity("success");
      }
      setNotify(true);
      getEnvs(showAllEnvs(toShowAllEnvs));
      setIsSavingUpdates(false);
    });
  };

  const discardUpdatesForEnv = (envId) => {
    const remainingUpdates = { ...updates };
    delete remainingUpdates[envId];
    setUpdates(remainingUpdates);
    checkIfUpdatesIsEmpty(remainingUpdates);
    setEnvsWithUpdates(
      mergeEnvsWithUpdates({ passedUpdates: remainingUpdates })
    );
  };

  // The read-only and editable drawers are mutually exclusive. Opening one
  // while the other has unsaved member/schedule edits discards those edits —
  // the same thing Cancel/X/scrim already do on the editable drawer today.
  const openViewDetails = (rowId) => {
    if (detailsEnvId) {
      discardUpdatesForEnv(detailsEnvId);
      setDetailsEnvId(null);
    }
    setViewDetailsEnvId(rowId);
  };
  const closeViewDetails = () => setViewDetailsEnvId(null);
  const openEditableDrawer = (rowId) => {
    if (viewDetailsEnvId) closeViewDetails();
    setDetailsEnvId(rowId);
  };

  const onSetUpdates = () => {
    checkIfUpdatesIsEmpty();
    setEnvsWithUpdates(mergeEnvsWithUpdates());
  };

  const addMember = (envId, email, role) => {
    setUpdates((prev) => {
      if (!prev[envId]?.membersAdded) {
        prev[envId] = {
          ...(prev[envId] && prev[envId]),
          membersAdded: new Map(),
          addedMembersIndex: [],
        };
      }
      if (prev[envId].membersDeleted) {
        prev[envId].membersDeleted.delete(email);
      }
      const env = envs[idToIndexMap[envId]];
      if (env?.indexedMembers?.includes(email)) {
        if (env?.members[email] !== role) {
          if (!prev[envId]?.membersModified) {
            prev[envId].membersModified = new Map();
          }
          prev[envId]?.membersModified?.set(email, role);
        }
      } else {
        prev[envId]?.membersAdded?.set(email, role);
        prev[envId]?.addedMembersIndex?.push(email);
      }
      return prev;
    });
    checkIfUpdatesIsEmpty();
    setEnvsWithUpdates(mergeEnvsWithUpdates());
  };

  const deleteMember = (envId, email) => {
    setUpdates((prev) => {
      if (!prev[envId]?.membersDeleted) {
        prev[envId] = {
          ...(prev[envId] && prev[envId]),
          membersDeleted: new Set(),
        };
      }
      prev[envId].membersDeleted.add(email);
      if (prev[envId]?.membersAdded) {
        prev[envId]?.membersAdded?.delete(email);
        prev[envId].addedMembersIndex = prev[envId]?.addedMembersIndex?.filter(
          (item) => item !== email
        );
      }
      if (prev[envId]?.membersModified) {
        prev[envId]?.membersModified?.delete(email);
      }
      if (!(email in envs[idToIndexMap[envId]].members)) {
        prev[envId]?.membersDeleted?.delete(email);
      }
      return prev;
    });
    checkIfUpdatesIsEmpty();
    setEnvsWithUpdates(mergeEnvsWithUpdates());
  };

  const modifyMemberRole = (envId, email, role) => {
    setUpdates((prev) => {
      if (!prev[envId]?.membersModified) {
        prev[envId] = {
          ...(prev[envId] && prev[envId]),
          membersModified: new Map(),
        };
      }
      if (prev[envId]?.membersAdded?.has(email)) {
        prev[envId]?.membersAdded?.set(email, role);
      } else {
        prev[envId]?.membersModified?.set(email, role);
      }
      return prev;
    });
    checkIfUpdatesIsEmpty();
    setEnvsWithUpdates(mergeEnvsWithUpdates());
  };

  const columns = [
    {
      field: "details",
      renderHeader: () => "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      disableReorder: true,
      width: 40,
      renderCell: ({ row }) => {
        const isOpen = row?.id === viewDetailsEnvId;
        return (
          <Tooltip title={isOpen ? "Hide details" : "View details"}>
            <IconButton
              size="small"
              sx={rowOpenStyles.chevronButton(colors, isOpen)}
              onClick={() =>
                isOpen ? closeViewDetails() : openViewDetails(row?.id)
              }
            >
              <ChevronRight sx={rowOpenStyles.chevronIcon(isOpen)} />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "name",
      flex: 3,
      cellClassName: "name-column--cell",
      renderHeader: () => "Name",
      hideable: false,
      disableReorder: true,
      renderCell: ({ row: { name, url } }) => {
        return url ? (
          <Link
            to={`https://${url}`}
            target="_blank"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            {name || ""}
          </Link>
        ) : (
          name || ""
        );
      },
    },
    {
      field: "status",
      renderHeader: () => "Status",
      width: 130,
      minWidth: 130,
      renderCell: ({ row }) =>
        refreshingEnvIds.has(row?.id) ? (
          <Box sx={indexStyles.refreshingPill(colors)}>
            <CircularProgress size={10} color="inherit" />
            REFRESHING
          </Box>
        ) : (
          <StatusChip value={row?.status} />
        ),
    },
    {
      field: "release_label",
      renderHeader: () => "Release",
      width: 130,
      minWidth: 130,
      sortComparator: compareReleaseLabels,
      renderCell: ({ row }) => {
        const label = row?.release_label;
        if (!label) return "-";
        // A failed update can leave services split across the old and new
        // release (Tessell upgrades never roll back), so the label is only
        // the attempted target here, not a confirmed running version.
        if (row?.status === "UPDATE_FAILED") {
          const { fg } = getStatusColors(colors, row.status);
          return (
            <Tooltip
              title={`Update to ${label} failed — some services may already be on this release, others may still be on the previous one`}
            >
              <span style={{ color: fg }}>{label} (failed)</span>
            </Tooltip>
          );
        }
        return <span>{label}</span>;
      },
    },
    {
      field: "controlPlaneCloud",
      renderHeader: () => (
        <Tooltip title="Control Plane Cloud">
          <span>C P Cloud</span>
        </Tooltip>
      ),
      minWidth: 80,
      width: 80,
      renderCell: ({ row }) => (
        <Tooltip title={row?.meta?.cloud_deployment_type || ""}>
          <Box sx={{ display: "flex", margin: "auto" }}>
            <CloudIcon cloud={row?.meta?.cloud_deployment_type} />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "createdBy",
      renderHeader: () => "Created By",
      flex: 2,
      renderCell: ({ row: { createdBy } }) => createdBy || "-",
    },
    {
      field: "memberRole",
      renderHeader: () => "User Role",
      width: 96,
      minWidth: 96,
      renderCell: ({ row: { memberRole } }) => memberRole || "-",
    },
    {
      field: "age",
      renderHeader: () => "Age",
      flex: 1,
      renderCell: ({ row: { age, createdOn } }) => (
        <Tooltip title={`Created on ${createdOn ?? "-"}`}>
          <span>{age || (age === 0 ? 0 : "-")} d</span>
        </Tooltip>
      ),
    },
    {
      field: "date_modified",
      renderHeader: () => "Last Modified At",
      flex: 1.8,
      minWidth: 150,
      renderCell: ({ row: { lastModifiedAt } }) => (
        <TimeCell value={lastModifiedAt} />
      ),
    },
    {
      field: "actions",
      renderHeader: () => "Actions",
      headerAlign: "left",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      disableReorder: true,
      // Fixed so the action group keeps the same footprint on every row instead
      // of stretching with the table. Widened by 34px to fit the Tune icon
      // relocated here from the old `details` column.
      width: 334,
      minWidth: 334,
      resizable: false,
      renderCell: ({ row }) => (
        <RowActions
          row={row}
          isRefreshing={refreshingEnvIds.has(row?.id)}
          handleHibernateWakeUp={handleHibernateWakeUp}
          handleRefresh={handleRefresh}
          handleDelete={handleDelete}
          handleConvertToVPTC={handleConvertToVPTC}
          handleAutomationRuns={handleAutomationRuns}
          handleAddLicences={handleAddLicences}
          onOpenEditable={(r) => openEditableDrawer(r?.id)}
          onViewDetails={(r) => openViewDetails(r?.id)}
        />
      ),
    },
  ];

  // Reorderable columns are everything between the pinned "details" (always
  // first) and "actions" (always last) columns. Apply any saved order to
  // that middle slice only, then re-append the pinned ends around it.
  const [firstColumn, ...restColumns] = columns;
  const lastColumn = restColumns.pop();
  const orderedMiddle = columnOrder
    ? columnOrder
        .map((field) => restColumns.find((c) => c.field === field))
        .filter(Boolean)
        .concat(restColumns.filter((c) => !columnOrder.includes(c.field)))
    : restColumns;
  const orderedColumns = [firstColumn, ...orderedMiddle, lastColumn];

  // "details"/"name"/"actions" are structural (row-open toggle, the row's own
  // identity, and row actions), not data the user would ever want to hide.
  const NON_TOGGLEABLE_COLUMNS = ["details", "name", "actions"];
  const toggleableColumns = orderedColumns
    .filter((column) => !NON_TOGGLEABLE_COLUMNS.includes(column.field))
    .map((column) => ({
      field: column.field,
      label: column.renderHeader ? column.renderHeader() : column.field,
    }));

  const detailsEnv = envsWithUpdates?.find((env) => env?.id === detailsEnvId);

  // Only one env is editable at a time: closing without saving drops that
  // env's pending edits so the next env starts from a clean slate.
  const closeDetailsDrawer = () => {
    discardUpdatesForEnv(detailsEnvId);
    setDetailsEnvId(null);
  };

  const saveDetailsDrawer = () => {
    saveUpdates(detailsEnvId);
    setDetailsEnvId(null);
  };

  const detailsDrawer = (
    <Drawer
      anchor="right"
      open={Boolean(detailsEnv)}
      onClose={closeDetailsDrawer}
      PaperProps={{
        sx: {
          ...indexStyles.detailsDrawerPaper(colors, detailsDrawerWidth),
          overflow: "visible",
        },
      }}
    >
      <DrawerResizeHandle
        {...detailsResizeProps}
        ariaLabel="Resize environment details drawer"
      />
      <DrawerSnapToggle
        enabled={isDesktopDrawer}
        isWide={isDetailsDrawerWide}
        onClick={toggleDetailsDrawerWide}
        ariaLabel="Expand environment details drawer to 75% width"
        collapseLabel="Collapse environment details drawer"
        sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
      />
      {detailsEnv ? (
        <>
          <Box sx={indexStyles.detailsDrawerHeader(colors)}>
            <Box>
              <Typography sx={indexStyles.detailsDrawerTitle(colors)}>
                {detailsEnv?.name}
              </Typography>
              <Typography sx={indexStyles.detailsDrawerSubtitle(colors)}>
                Members and scheduled configurations
              </Typography>
            </Box>
            <IconButton
              sx={closeButtonStyles(colors)}
              onClick={closeDetailsDrawer}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={indexStyles.detailsDrawerBody}>
            <Box sx={indexStyles.detailsDrawerMembersPane}>
              <MembersList
                env={detailsEnv}
                addMember={addMember}
                deleteMember={deleteMember}
                modifyMemberRole={modifyMemberRole}
              />
            </Box>
            <Box sx={indexStyles.detailsDrawerSchedulePane(colors)}>
              <ScheduledConfigs
                env={detailsEnv}
                envOrig={envs[idToIndexMap[detailsEnv?.id]]}
                setUpdates={setUpdates}
                onSetUpdates={onSetUpdates}
              />
            </Box>
          </Box>
          <Box sx={indexStyles.detailsDrawerFooter(colors)}>
            <Typography sx={indexStyles.detailsDrawerFooterNote(colors)}>
              {updates[detailsEnvId]?.hasUpdates
                ? "Unsaved changes"
                : "No changes"}
            </Typography>
            <Box sx={indexStyles.detailsDrawerFooterActions}>
              <DataGridButton
                sx={indexStyles.detailsDrawerCancelButton(colors)}
                onClick={closeDetailsDrawer}
              >
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  CANCEL
                </Typography>
              </DataGridButton>
              <DataGridButton
                sx={indexStyles.detailsDrawerButton}
                disabled={!updates[detailsEnvId]?.hasUpdates || isSavingUpdates}
                onClick={saveDetailsDrawer}
              >
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  SAVE
                </Typography>
              </DataGridButton>
            </Box>
          </Box>
        </>
      ) : null}
    </Drawer>
  );

  const requestCPDrawer = (
    <Drawer
      anchor="right"
      open={requestCPDrawerOpen}
      onClose={closeRequestCPDrawer}
      PaperProps={{
        sx: {
          ...requestCPDialogStyles.drawerPaper(colors, requestCPDrawerWidth),
          overflow: "visible",
        },
      }}
    >
      <DrawerResizeHandle
        {...requestCPResizeProps}
        ariaLabel="Resize request control plane drawer"
      />
      <DrawerSnapToggle
        enabled={isDesktopDrawer}
        isWide={isRequestCPDrawerWide}
        onClick={toggleRequestCPDrawerWide}
        ariaLabel="Expand request control plane drawer to 75% width"
        collapseLabel="Collapse request control plane drawer"
        sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
      />
      <Box sx={requestCPDialogStyles.drawerHeader(colors)}>
        <Box>
          <Typography sx={requestCPDialogStyles.drawerTitle(colors)}>
            Request Control Plane
          </Typography>
          <Typography sx={requestCPDialogStyles.drawerSubtitle(colors)}>
            Provision a new control plane environment
          </Typography>
        </Box>
        <IconButton
          sx={closeButtonStyles(colors)}
          onClick={closeRequestCPDrawer}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={requestCPDialogStyles.drawerBody}>
        {requestCPDrawerOpen ? (
          <RequestCPDialogContent
            requestCPPayload={requestCPPayload}
            setRequestCPPayload={setRequestCPPayload}
          />
        ) : null}
      </Box>
      <Box sx={requestCPDialogStyles.drawerFooter(colors)}>
        <DataGridButton
          sx={requestCPDialogStyles.drawerCancelButton(colors)}
          onClick={closeRequestCPDrawer}
        >
          <Typography sx={dataGridButtonTypography(colors, 500)}>
            CANCEL
          </Typography>
        </DataGridButton>
        <DataGridButton
          sx={requestCPDialogStyles.drawerButton}
          disabled={
            !isValidRequestCPPayload(requestCPPayload) || isSubmittingCP
          }
          onClick={submitRequestCP}
        >
          <Typography sx={dataGridButtonTypography(colors, 500)}>
            SUBMIT
          </Typography>
        </DataGridButton>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={indexStyles.mainBox}>
      <Notification
        notify={notify}
        severity={severity}
        setNotify={setNotify}
        message={message}
      />
      <ConvoyDialog
        open={dialog?.isOpen}
        title={dialog?.title}
        handleDialogConfirm={handleDialogConfirm}
        resetDialog={resetDialog}
        variant={dialog?.context === "wakeUp" ? "confirmation" : undefined}
      >
        {(() => {
          switch (dialog.context) {
            case "wakeUp":
              return (
                <WakeupDialogContent
                  env={dialog?.selectedEnv}
                  setRefreshDialog={setRefreshDialog}
                  setDialog={setDialog}
                />
              );
            case "requestBYOA":
              return (
                <RequestBYOADialogContent
                  env={dialog?.selectedEnv}
                  requestBYOAPayload={requestBYOAPayload}
                  setRequestBYOAPayload={setRequestBYOAPayload}
                />
              );
            default:
              return <></>;
          }
        })()}
      </ConvoyDialog>
      <RefreshDrawer
        refreshDialog={refreshDialog}
        setRefreshDialog={setRefreshDialog}
        refreshEnv={handleRefreshSubmit}
        refreshState={refreshEnvState}
      />
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        title={confirmationDialog.title}
        handleDialogConfirm={confirmationDialog.handleDialogConfirm}
        content={confirmationDialog.content}
        resetDialog={resetDialog}
      />
      <BYOASubscriptionsModal
        byoaModal={byoaModal}
        setByoaModal={setByoaModal}
      />
      {detailsDrawer}
      {requestCPDrawer}
      <DetailsDrawer
        envId={viewDetailsEnvId}
        envs={envsWithUpdates}
        onClose={closeViewDetails}
        currentUserEmail={loggedInUser?.email}
      />
      <Grid sx={indexStyles.gridContainer} container columns={2}>
        <Grid xs={1}>
          <Header
            title="Environments"
            subtitle="Manage the environments you have access to"
          />
        </Grid>
        <Grid xs={1}>
          <Stack direction={"row-reverse"} spacing={1} alignItems="center">
            <DataGridButton
              sx={{
                ...dataGridButtonStyles.pageTopButtons,
                ...envListButtonStyles(colors).toolbarPrimary,
              }}
              onClick={handleRequestCP}
              toolTipProps={{ title: "Request a new environment" }}
            >
              <Add sx={{ fontSize: 16, color: "#fff" }} />
              <Typography>CREATE NEW</Typography>
            </DataGridButton>
            <DataGridButton
              sx={{
                ...dataGridButtonStyles.pageTopButtons,
                ...envListButtonStyles(colors).toolbarOutlined,
              }}
              onClick={() => getEnvs(showAllEnvs(toShowAllEnvs))}
              toolTipProps={{ title: "Reload the environments list" }}
            >
              <Sync sx={{ fontSize: 16 }} />
              <Typography>RELOAD</Typography>
            </DataGridButton>
            {false ? (
              <DataGridButton
                sx={dataGridButtonStyles.pageTopButtons}
                onClick={() => setByoaModal({ isOpen: true })}
              >
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  BYOA Subs
                </Typography>
              </DataGridButton>
            ) : (
              <></>
            )}
            <Tooltip title="Include environments you are not a member of (read-only)">
              <Stack
                direction={"row-reverse"}
                alignItems="center"
                justifyContent="center"
                sx={indexStyles.showAllEnvsStack}
              >
                <Box sx={indexStyles.showAllEnvsBox(colors)}>Show all envs</Box>
                <Switch
                  checked={toShowAllEnvs}
                  size="small"
                  onChange={() => setToShowAllEnvs(!toShowAllEnvs)}
                />
              </Stack>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={indexStyles.filtersStack}>
        <SearchWithHistory
          value={searchText}
          onChange={setSearchText}
          storageKey={envFilterCacheKeys.searchHistory}
          suggestions={nameOptions}
          suggestionsGroupLabel="Environments"
          sx={indexStyles.searchField}
        />
        <FormControl size="small" sx={indexStyles.filterSelect}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value={ALL_FILTER}>ALL</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {getStatusLabel(status)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          sx={indexStyles.filterSelect}
          options={[ALL_FILTER, ...createdByOptions]}
          value={createdByFilter}
          onChange={(_, v) => setCreatedByFilter(v || ALL_FILTER)}
          disableClearable
          autoHighlight
          renderInput={(params) => <TextField {...params} label="Created By" />}
        />
        <Box sx={indexStyles.envSummary(colors)}>
          <span>{statusCounts.TOTAL || 0} environments</span>
          <span>·</span>
          <Box component="span" sx={indexStyles.envSummaryRunning(colors)}>
            {statusCounts.RUNNING || 0} Running
          </Box>
          <span>·</span>
          <span>{statusCounts.HIBERNATED || 0} Hibernated</span>
          <span>·</span>
          <Box component="span" sx={indexStyles.envSummaryFailed(colors)}>
            {statusCounts.FAILED || 0} Failed
          </Box>
          <span>·</span>
          <span>{statusCounts.OTHERS || 0} Others</span>
        </Box>
      </Box>

      <Box sx={{ position: "relative", width: "99.2%" }}>
        <Box sx={indexStyles.columnPickerCorner}>
          <ColumnVisibilityMenu
            columns={toggleableColumns}
            value={columnVisibilityModel}
            onChange={handleColumnVisibilityChange}
            onReorder={handleMenuReorder}
            onReset={handleResetColumnSettings}
          />
        </Box>
        <DataTable
          hideFooter
          height="80vh"
          sx={rowOpenStyles.gridRowOpenRule(colors)}
          loading={isLoadingEnvs || isSavingUpdates}
          rows={filteredEnvs}
          columns={orderedColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          onColumnOrderChange={handleColumnOrderChange}
          getRowClassName={(params) =>
            params.id === viewDetailsEnvId ? "env-row-open" : ""
          }
        />
      </Box>
    </Box>
  );
};

export default Environments;
